// ============================================================================
// Report files from the server (PMS slice 5 — the Fairbooks way)
// ============================================================================
// The API builds the formatted file (ReportExportService: .xlsx via ClosedXML,
// PDF via QuestPDF). Excel is saved; the PDF opens in a new tab where the
// browser's viewer prints it — the same pages in every browser.
//
// The tab for a PDF is opened synchronously in the click handler and filled when
// the file arrives: a window opened after an await is treated as a pop-up and
// blocked, which is how a print button "does nothing".

export type ReportFormat = 'xlsx' | 'pdf';

/** Opens a blank tab now (inside the click) so the PDF can be shown in it later. */
export const reserveTab = (): Window | null => {
  const tab = window.open('', '_blank');
  if (tab) {
    tab.document.title = 'Preparing report…';
    tab.document.body.innerHTML = '<p style="font-family:sans-serif;color:#64748b;padding:2rem">Preparing report…</p>';
  }
  return tab;
};

/** File name from Content-Disposition, else the fallback. */
const fileNameOf = (disposition: string | undefined, fallback: string): string => {
  const m = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return m ? decodeURIComponent(m[1]) : fallback;
};

export const saveReportFile = (blob: Blob, disposition: string | undefined, fallback: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileNameOf(disposition, fallback);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/** Shows a PDF in the reserved tab (or a new one when none could be reserved). */
export const showPdf = (blob: Blob, tab: Window | null): void => {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60000);
};

/** An error body arrives as a Blob when responseType is blob — read its message. */
export const blobErrorMessage = async (err: any, fallback: string): Promise<string> => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || err?.message || fallback;
};
